import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import {
  IViewersObjectBoolean,
  IViewersObjectColorData,
  IViewersObjectNumber,
  IViewersObjectNumbersData,
} from '@viewer/cornerstone.data';
import {
  d3ViewTools,
  d3ViewToolsType,
} from '@viewer/occ-viewer/occ-viewer.data';
import {
  BoneModelType,
  CornerstoneTool,
  PolylineModeType,
  ViewWindowIndex,
} from '@viewer/data';
import { PointerShape, IBrushSettings } from '../edit-image-tools/data';
import { ToolMenu } from '../edit-image-tools/data/tool-menu';
import { IClaimantDetailsDto } from '@viewer/services/project-actions.data';
import {
  BIG_MODEL_REQUEST_DELAY,
  DEFAULT_NUMBER_OF_ATTEMPTS,
  IRequestDelayData,
  MODEL_REQUEST_DELAY,
} from '@viewer/services/request-delay.data';
import { TypedArray } from '@shared/data';

/**
 * Service is intended to manage the application state at the moment
 * of being in the project
 */
@Injectable()
export class StateManagerProjectService {
  //#region caseId
  private _caseId$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public set caseId(value: string) {
    this._caseId$.next(value);
  }

  public get caseId(): string {
    return this._caseId$.getValue();
  }

  public get caseId$(): Observable<string> {
    return this._caseId$.asObservable();
  }
  //#endregion

  //#region activeShapeOfBrush
  private _currentSelectedWindow$: BehaviorSubject<ViewWindowIndex> =
    new BehaviorSubject<ViewWindowIndex>(ViewWindowIndex.Empty);

  public set currentSelectedWindow(value: ViewWindowIndex) {
    this._currentSelectedWindow$.next(value);
  }

  public get currentSelectedWindow(): ViewWindowIndex {
    return this._currentSelectedWindow$.getValue();
  }

  public get currentSelectedWindow$(): Observable<ViewWindowIndex> {
    return this._currentSelectedWindow$.asObservable();
  }
  //#endregion

  //#region isProcessOfResetDiff
  // A state that determines the active process of reset changes
  private _isProcessOfResetDiff$: BehaviorSubject<IViewersObjectBoolean> =
    new BehaviorSubject<IViewersObjectBoolean>({
      viewer1: false,
      viewer2: false,
      viewer3: false,
    });

  public set isProcessOfResetDiff(value: IViewersObjectBoolean) {
    this._isProcessOfResetDiff$.next(value);
  }

  public get isProcessOfResetDiff(): IViewersObjectBoolean {
    return this._isProcessOfResetDiff$.getValue();
  }

  public get isProcessOfResetDiff$(): Observable<IViewersObjectBoolean> {
    return this._isProcessOfResetDiff$.asObservable();
  }
  //#endregion

  //#region isProcessOfShowDiffHide
  // A state that determines the active process of hide show diff mode
  private _isProcessOfShowDiffHide$: BehaviorSubject<IViewersObjectBoolean> =
    new BehaviorSubject<IViewersObjectBoolean>({
      viewer1: false,
      viewer2: false,
      viewer3: false,
    });
  private _isViewChangeClickedReload$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private _isBoneViewLoaded$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  public set isViewChangeClickedReload(value: boolean | null) {
    this._isViewChangeClickedReload$.next(value);
  }

  public get isViewChangeClickedReload(): boolean | null {
    return this._isViewChangeClickedReload$.getValue();
  }

  public get isViewChangeClickedReload$(): Observable<boolean | null> {
    return this._isViewChangeClickedReload$.asObservable();
  }

  public set isBoneViewLoaded(value: boolean | null) {
    this._isBoneViewLoaded$.next(value);
  }

  public get isBoneViewLoaded(): boolean | null {
    return this._isBoneViewLoaded$.getValue();
  }

  public get isBoneViewLoaded$(): Observable<boolean | null> {
    return this._isBoneViewLoaded$.asObservable();
  }

  public set isProcessOfShowDiffHide(value: IViewersObjectBoolean) {
    this._isProcessOfShowDiffHide$.next(value);
  }

  public get isProcessOfShowDiffHide(): IViewersObjectBoolean {
    return this._isProcessOfShowDiffHide$.getValue();
  }

  public get isProcessOfShowDiffHide$(): Observable<IViewersObjectBoolean> {
    return this._isProcessOfShowDiffHide$.asObservable();
  }
  //#endregion

  //#region currentSelectedWindowElement
  // A state that determines html element of the selected window
  private _currentSelectedWindowElement$ =
    new BehaviorSubject<HTMLElement | null>(null);

  public set currentSelectedWindowElement(value: HTMLElement | null) {
    this._currentSelectedWindowElement$.next(value);
  }

  public get currentSelectedWindowElement(): HTMLElement | null {
    return this._currentSelectedWindowElement$.getValue();
  }

  public get currentSelectedWindowElement$(): Observable<HTMLElement | null> {
    return this._currentSelectedWindowElement$.asObservable();
  }
  //#endregion

  //#region isActiveShowChangesMode
  // A state that determines the active show changes mode
  private _isActiveShowChangesMode$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isActiveShowChangesMode(value: boolean) {
    this._isActiveShowChangesMode$.next(value);
  }

  public get isActiveShowChangesMode(): boolean {
    return this._isActiveShowChangesMode$.getValue();
  }

  public get isActiveShowChangesMode$(): Observable<boolean> {
    return this._isActiveShowChangesMode$.asObservable();
  }
  //#endregion

  //#region isChangesBetweenMasks
  // A state that determines the changes between based and changed masks
  private _isChangesBetweenMasks$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isChangesBetweenMasks(value: boolean) {
    this._isChangesBetweenMasks$.next(value);
  }

  public get isChangesBetweenMasks(): boolean {
    return this._isChangesBetweenMasks$.getValue();
  }

  public get isChangesBetweenMasks$(): Observable<boolean> {
    return this._isChangesBetweenMasks$.asObservable();
  }
  //#endregion

  //#region currentActiveSlice
  // A state that determines the active slice
  private _currentActiveSlice$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  public set currentActiveSlice(value: string) {
    this._currentActiveSlice$.next(value);
  }

  public get currentActiveSlice(): string {
    return this._currentActiveSlice$.getValue();
  }

  public get currentActiveSlice$(): Observable<string> {
    return this._currentActiveSlice$.asObservable();
  }
  //#endregion

  //#region currentActiveColor
  // A state that determines the active color
  private _currentActiveColor$: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(null);

  public set currentActiveColor(value: number | null) {
    this._currentActiveColor$.next(value);
  }

  public get currentActiveColor(): number | null {
    return this._currentActiveColor$.getValue();
  }

  public get currentActiveColor$(): Observable<number | null> {
    return this._currentActiveColor$.asObservable();
  }
  //#endregion

  //#region colorsData
  // A state that determines data of all colors
  private _colorsData$: BehaviorSubject<IViewersObjectColorData | null> =
    new BehaviorSubject<IViewersObjectColorData | null>(null);

  public set colorsData(value: IViewersObjectColorData | null) {
    this._colorsData$.next(value);
  }

  public get colorsData(): IViewersObjectColorData | null {
    return this._colorsData$.getValue();
  }

  public get colorsData$(): Observable<IViewersObjectColorData | null> {
    return this._colorsData$.asObservable();
  }
  //#endregion

  //#region hidenColors
  // A state that determines data of hiden colors
  private _hidenColors$: BehaviorSubject<IViewersObjectNumbersData | null> =
    new BehaviorSubject<IViewersObjectNumbersData | null>({
      viewer1: [],
      viewer2: [],
      viewer3: [],
    });

  public set hidenColors(value: IViewersObjectNumbersData | null) {
    this._hidenColors$.next(value);
  }

  public get hidenColors(): IViewersObjectNumbersData | null {
    return this._hidenColors$.getValue();
  }

  public get hidenColors$(): Observable<IViewersObjectNumbersData | null> {
    return this._hidenColors$.asObservable();
  }
  //#endregion

  //#region currentStateOfToolMenu
  // A state that determines the tool menu opened
  private _currentStateOfToolMenu$: BehaviorSubject<ToolMenu | null> =
    new BehaviorSubject<ToolMenu | null>(null);

  public set currentStateOfToolMenu(value: ToolMenu | null) {
    this._currentStateOfToolMenu$.next(value);
  }

  public get currentStateOfToolMenu(): ToolMenu | null {
    return this._currentStateOfToolMenu$.getValue();
  }

  public get currentStateOfToolMenu$(): Observable<ToolMenu | null> {
    return this._currentStateOfToolMenu$.asObservable();
  }
  //#endregion

  //#region currentTool
  // A state that determines the active tool
  private _currentTool$: BehaviorSubject<CornerstoneTool | null> =
    new BehaviorSubject<CornerstoneTool | null>(null);

  public set currentTool(value: CornerstoneTool | null) {
    this._currentTool$.next(value);
  }

  public get currentTool(): CornerstoneTool | null {
    return this._currentTool$.getValue();
  }

  public get currentTool$(): Observable<CornerstoneTool | null> {
    return this._currentTool$.asObservable();
  }
  //#endregion

  //#region current3dTool
  // A state that determines the active 3d tool
  private _current3dTool$: BehaviorSubject<d3ViewToolsType> =
    new BehaviorSubject<d3ViewToolsType>(null);

  public set current3dTool(value: d3ViewToolsType) {
    this._current3dTool$.next(value);
  }

  public get current3dTool(): d3ViewToolsType {
    return this._current3dTool$.getValue();
  }

  public get current3dTool$(): Observable<d3ViewToolsType> {
    return this._current3dTool$.asObservable();
  }
  //#endregion

  //#region isViewerLoaded
  // A state that determines the viewer is loaded
  private _isViewerLoaded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isViewerLoaded(value: boolean) {
    this._isViewerLoaded$.next(value);
  }

  public get isViewerLoaded(): boolean {
    return this._isViewerLoaded$.getValue();
  }

  public get isViewerLoaded$(): Observable<boolean> {
    return this._isViewerLoaded$.asObservable();
  }
  //#endregion

  //#region totalNumberOfSlices
  // A state that determines the total number of slices
  private _totalNumberOfSlices$: BehaviorSubject<IViewersObjectNumber> =
    new BehaviorSubject<IViewersObjectNumber>({
      viewer1: 0,
      viewer2: 0,
      viewer3: 0,
    });

  public set totalNumberOfSlices(value: IViewersObjectNumber) {
    this._totalNumberOfSlices$.next(value);
  }

  public get totalNumberOfSlices(): IViewersObjectNumber {
    return this._totalNumberOfSlices$.getValue();
  }

  public get totalNumberOfSlices$(): Observable<IViewersObjectNumber> {
    return this._totalNumberOfSlices$.asObservable();
  }
  //#endregion

  //#region currentActiveSliceIndex
  // A state that determines the active slice index
  private _currentActiveSliceIndex$: BehaviorSubject<IViewersObjectNumber> =
    new BehaviorSubject<IViewersObjectNumber>({
      viewer1: 0,
      viewer2: 0,
      viewer3: 0,
    });

  public set currentActiveSliceIndex(value: IViewersObjectNumber) {
    this._currentActiveSliceIndex$.next(value);
  }

  public get currentActiveSliceIndex(): IViewersObjectNumber {
    return this._currentActiveSliceIndex$.getValue();
  }

  public get currentActiveSliceIndex$(): Observable<IViewersObjectNumber> {
    return this._currentActiveSliceIndex$.asObservable();
  }
  //#endregion

  //#region viewMode
  private _viewMode$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public set viewMode(value: number) {
    this._viewMode$.next(value);
  }

  public get viewMode(): number {
    return this._viewMode$.getValue();
  }

  public get viewMode$(): Observable<number> {
    return this._viewMode$.asObservable();
  }
  //#endregion

  //#region currentPolylineMode
  private _currentPolylineMode$ = new BehaviorSubject<PolylineModeType>(
    'DEFAULT'
  );

  public set currentPolylineMode(value: PolylineModeType) {
    this._currentPolylineMode$.next(value);
  }

  public get currentPolylineMode(): PolylineModeType {
    return this._currentPolylineMode$.getValue();
  }

  public get currentPolylineMode$(): Observable<PolylineModeType> {
    return this._currentPolylineMode$.asObservable();
  }
  //#endregion

  //#region brushSettings
  private _brushSettings$ = new BehaviorSubject<IBrushSettings>({
    size: 10,
    shape: PointerShape.Circle,
  });

  public set brushSettings(value: IBrushSettings) {
    this._brushSettings$.next(value);
  }

  public get brushSettings(): IBrushSettings {
    return this._brushSettings$.getValue();
  }

  public get brushSettings$(): Observable<IBrushSettings> {
    return this._brushSettings$.asObservable();
  }
  //#endregion

  //#region eraserSettings
  private _eraserSettings$ = new BehaviorSubject<IBrushSettings>({
    size: 10,
    shape: PointerShape.Circle,
  });

  public set eraserSettings(value: IBrushSettings) {
    this._eraserSettings$.next(value);
  }

  public get eraserSettings(): IBrushSettings {
    return this._eraserSettings$.getValue();
  }

  public get eraserSettings$(): Observable<IBrushSettings> {
    return this._eraserSettings$.asObservable();
  }
  //#endregion

  //#region is3dUpdateNeeded
  private _is3dUpdateNeeded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public get is3dUpdateNeeded() {
    return this._is3dUpdateNeeded$.getValue();
  }

  public set is3dUpdateNeeded(value: boolean) {
    this._is3dUpdateNeeded$.next(value);
  }
  //#endregion

  //#region isSaveActionDisabled
  // A state that determines that save button disabled
  private _isSaveActionDisabled$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isSaveActionDisabled(value: boolean) {
    this._isSaveActionDisabled$.next(value);
  }

  public get isSaveActionDisabled(): boolean {
    return this._isSaveActionDisabled$.getValue();
  }

  public get isSaveActionDisabled$(): Observable<boolean> {
    return this._isSaveActionDisabled$.asObservable();
  }
  //#endregion

  //#region boneModelType
  private _boneModelType$: BehaviorSubject<BoneModelType | null> =
    new BehaviorSubject<BoneModelType | null>(null);

  public set boneModelType(value: BoneModelType | null) {
    this._boneModelType$.next(value);
  }

  public get boneModelType(): BoneModelType | null {
    return this._boneModelType$.getValue();
  }

  public get boneModelType$(): Observable<BoneModelType | null> {
    return this._boneModelType$.asObservable();
  }
  //#endregion

  //#region isDisabledAllToolsForChangeMask
  // A state that determines that all tools for change mask must be disabled
  private _isDisabledAllToolsForChangeMask$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isDisabledAllToolsForChangeMask(value: boolean) {
    this._isDisabledAllToolsForChangeMask$.next(value);
  }

  public get isDisabledAllToolsForChangeMask(): boolean {
    return this._isDisabledAllToolsForChangeMask$.getValue();
  }

  public get isDisabledAllToolsForChangeMask$(): Observable<boolean> {
    return this._isDisabledAllToolsForChangeMask$.asObservable();
  }
  //#endregion

  //#region isEditAllowed
  // A state that determines that all tools for change mask must be disabled
  private _isEditAllowed$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isEditAllowed(value: boolean) {
    this._isEditAllowed$.next(value);
  }

  public get isEditAllowed(): boolean {
    return this._isEditAllowed$.getValue();
  }

  public get isEditAllowed$(): Observable<boolean> {
    return this._isEditAllowed$.asObservable();
  }
  //#endregion

    //#region isApprovedClicked
  // A state that determines that all tools for change mask must be disabled
  private _isApprovedClicked$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isApprovedClicked(value: boolean) {
    this._isApprovedClicked$.next(value);
  }

  public get isApprovedClicked(): boolean {
    return this._isApprovedClicked$.getValue();
  }

  public get isApprovedClicked$(): Observable<boolean> {
    return this._isApprovedClicked$.asObservable();
  }
  //#endregion

  //#region isRejectClicked
  // A state that determines that all tools for change mask must be disabled
  private _isRejectClicked$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isRejectClicked(value: boolean) {
    this._isRejectClicked$.next(value);
  }

  public get isRejectClicked(): boolean {
    return this._isRejectClicked$.getValue();
  }

  public get isRejectClicked$(): Observable<boolean> {
    return this._isRejectClicked$.asObservable();
  }
  //#endregion

  //#region isCaseApproved
  // A state that determines that all tools for change mask must be disabled
  private _isCaseApproved$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isCaseApproved(value: boolean) {
    this._isCaseApproved$.next(value);
  }

  public get isCaseApproved(): boolean {
    return this._isCaseApproved$.getValue();
  }

  public get isCaseApproved$(): Observable<boolean> {
    return this._isCaseApproved$.asObservable();
  }
  //#endregion

  //#region isCaseRejected
  // A state that determines that all tools for change mask must be disabled
  private _isCaseRejected$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isCaseRejected(value: boolean) {
    this._isCaseRejected$.next(value);
  }

  public get isCaseRejected(): boolean {
    return this._isCaseRejected$.getValue();
  }

  public get isCaseRejected$(): Observable<boolean> {
    return this._isCaseRejected$.asObservable();
  }
  //#endregion

  //#region isPanelForClaimantUserVisible
  // A state that determines that panel for claimant user is visible
  private _isPanelForClaimantUserVisible$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isPanelForClaimantUserVisible(value: boolean) {
    this._isPanelForClaimantUserVisible$.next(value);
  }

  public get isPanelForClaimantUserVisible(): boolean {
    return this._isPanelForClaimantUserVisible$.getValue();
  }

  public get isPanelForClaimantUserVisible$(): Observable<boolean> {
    return this._isPanelForClaimantUserVisible$.asObservable();
  }
  //#endregion

  //#region claimantUser
  // A state that determines info of claimant user
  private _claimantUser$: BehaviorSubject<IClaimantDetailsDto | undefined> =
    new BehaviorSubject<IClaimantDetailsDto | undefined>(undefined);

  public set claimantUser(value: IClaimantDetailsDto | undefined) {
    this._claimantUser$.next(value);
  }

  public get claimantUser(): IClaimantDetailsDto | undefined {
    return this._claimantUser$.getValue();
  }

  public get claimantUser$(): Observable<IClaimantDetailsDto | undefined> {
    return this._claimantUser$.asObservable();
  }
  //#endregion

  //#region currentUserName
  // A state that determines info of current UserName
  private _currentUserName$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  public set currentUserName(value: string) {
    this._currentUserName$.next(value);
  }

  public get currentUserName(): string {
    return this._currentUserName$.getValue();
  }

  public get currentUserName$(): Observable<string> {
    return this._currentUserName$.asObservable();
  }
  //#endregion

  //#region mouseWasDragged
  // A state that determines mouse was dragged
  private _mouseWasDragged$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set mouseWasDragged(value: boolean) {
    this._mouseWasDragged$.next(value);
  }

  public get mouseWasDragged(): boolean {
    return this._mouseWasDragged$.getValue();
  }

  public get mouseWasDragged$(): Observable<boolean> {
    return this._mouseWasDragged$.asObservable();
  }
  //#endregion

  //#region maskNrrdType
  // A state that determines mouse was dragged
  private _maskNrrdType$: BehaviorSubject<string> = new BehaviorSubject<string>(
    ''
  );

  public set maskNrrdType(value: string) {
    this._maskNrrdType$.next(value);
  }

  public get maskNrrdType(): string {
    return this._maskNrrdType$.getValue();
  }

  public get maskNrrdType$(): Observable<string> {
    return this._maskNrrdType$.asObservable();
  }
  //#endregion

  //#region isAlreadyClosedApp
  // A state that determines that app was already closed
  private _isAlreadyClosedApp$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isAlreadyClosedApp(value: boolean) {
    this._isAlreadyClosedApp$.next(value);
  }

  public get isAlreadyClosedApp(): boolean {
    return this._isAlreadyClosedApp$.getValue();
  }

  public get isAlreadyClosedApp$(): Observable<boolean> {
    return this._isAlreadyClosedApp$.asObservable();
  }
  //#endregion

  //#region requestDelayData
  // A state that determines the request delay settings
  private _requestDelayData$: BehaviorSubject<IRequestDelayData> =
    new BehaviorSubject<IRequestDelayData>({
      modelRequestDelay: MODEL_REQUEST_DELAY,
      bigModelRequestDelay: BIG_MODEL_REQUEST_DELAY,
      defaultNumberOfAttempts: DEFAULT_NUMBER_OF_ATTEMPTS,
    });

  public set requestDelayData(value: IRequestDelayData) {
    this._requestDelayData$.next(value);
  }

  public get requestDelayData(): IRequestDelayData {
    return this._requestDelayData$.getValue();
  }

  public get requestDelayData$(): Observable<IRequestDelayData> {
    return this._requestDelayData$.asObservable();
  }
  //#endregion

  //#region isMaskWasChanged
  // A state that determines the mask was changed
  private _isMaskWasChanged$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public set isMaskWasChanged(value: boolean) {
    this._isMaskWasChanged$.next(value);
  }

  public get isMaskWasChanged(): boolean {
    return this._isMaskWasChanged$.getValue();
  }

  public get isMaskWasChanged$(): Observable<boolean> {
    return this._isMaskWasChanged$.asObservable();
  }
  //#endregion

  /** 
   * caseType state with either femur/tibia value during open/view action
   */ 
  private _caseType: string = '';

  public set caseType(type: string) {
    this._caseType = type;
  }

  public get caseType() {
    return this._caseType;
  }

  /** 
   * caseImagingType state to identify tibia MRI/CT during case open/view action
   */ 
  private _caseImagingType: string = '';

  public set caseImagingType(type: string) {
    this._caseImagingType = type;
  }

  public get caseImagingType() {
    return this._caseImagingType;
  }

  /** 
   * _basePixelData which stores the original pixel data of the image loaded via nrrd file
   */ 
  private _basePixelData: TypedArray | null = null;

  public set basePixelData(value: TypedArray) {
    this._basePixelData = value;
  }

  public get basePixelData(): TypedArray | null {
    return this._basePixelData;
  }

  constructor() {
    this.init();
    this._registerInGlobalScope();
  }

  init(): void {
    this.isPanelForClaimantUserVisible = false;
    this.claimantUser = undefined;
    this.currentUserName = '';
    this.currentSelectedWindow = ViewWindowIndex.Empty;
    this.currentSelectedWindowElement = null;
    this.isDisabledAllToolsForChangeMask = false;
    this.isEditAllowed = false;
    this.isProcessOfResetDiff = {
      viewer1: false,
      viewer2: false,
      viewer3: false,
    };

    this.isProcessOfShowDiffHide = {
      viewer1: false,
      viewer2: false,
      viewer3: false,
    };

    this.isActiveShowChangesMode = false;
    this.currentActiveSlice = '';
    this.currentActiveColor = null;
    this.colorsData = {
      viewer1: [],
      viewer2: [],
      viewer3: [],
    };

    this.hidenColors = {
      viewer1: [],
      viewer2: [],
      viewer3: [],
    };

    this.currentStateOfToolMenu = null;
    this.currentTool = null;
    this.current3dTool = d3ViewTools[3];
    this.isViewerLoaded = false;
    this.totalNumberOfSlices = {
      viewer1: 0,
      viewer2: 0,
      viewer3: 0,
    };

    this.currentActiveSliceIndex = {
      viewer1: 0,
      viewer2: 0,
      viewer3: 0,
    };

    this.viewMode = 4;
    this.currentPolylineMode = 'DEFAULT';
    this.brushSettings = {
      size: 10,
      shape: PointerShape.Circle,
    };

    this.eraserSettings = {
      size: 10,
      shape: PointerShape.Circle,
    };

    this.isChangesBetweenMasks = false;
    this.isSaveActionDisabled = true;
    this.is3dUpdateNeeded = false;
    this.boneModelType = null;
    this.mouseWasDragged = false;
    this.maskNrrdType = '';
    this.isAlreadyClosedApp = false;
    this.isMaskWasChanged = false;
  }

  getSelectedViewerId(): string {
    return `viewer${this._currentSelectedWindow$.getValue()}`;
  }

  // This is not a very good solution, it is necessary to have access
  // to the methods of this service in the code of the .js files
  // This change is done as a fix for this bug: The Brush/Lasso/Eraser tools appears in all views at the same time
  // Jira link: https://snifreeksky.atlassian.net/browse/PRSAMD-2004
  private _registerInGlobalScope(): void {
    const exportFunctions = {
      getSelectedViewerId: this.getSelectedViewerId.bind(this),
    };

    const angularPropStr = 'angular';
    window[angularPropStr] = window[angularPropStr] || {};
    window[angularPropStr].stateManagerProjectService = exportFunctions;
  }
}
